import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { inject, observer } from 'mobx-react'
import { reaction, observable } from 'mobx'
import get from 'lodash/get'
import { NULL as BlockModelNULL } from '@client/components/Block/model'

import ImageModel, { NULL as ImageModelNULL } from './model'
import AppModel, { NULL as AppModelNULL } from '@client/components/App/model'
import EditorModel, {
  NULL as EditorModelNULL,
} from '@client/components/Editor/model'
import { SECTION_TYPES } from '@client/components/Section/constants'
import { getPaddingTotal } from '@client/components/Block/component'
import { isHttpImageUrl } from '@client/utils/helpers'
import reservedImageHeight from '@client/utils/reservedImageHeight'

export const IMAGE_PLACEHOLDER_PATH = 'https://cdn.getanewsletter.com/blockeditor/placeholder-image.png'

const getPaddingReduction = ({ kind, section, columnIndex, fullWidth }) => {
  if (fullWidth) {
    return 0
  }
  const sectionType = get(section, 'sectionType')
  if (
    sectionType === SECTION_TYPES.LANDINGONECOL ||
    sectionType === SECTION_TYPES.LANDINGTWOCOL
  ) {
    return 0
  }

  return getPaddingTotal({ kind, section, columnIndex, fullWidth })
}

export class Image extends Component {
  @observable editingSettings = BlockModelNULL

  state = { imgError: false }

  static propTypes = {
    model: PropTypes.instanceOf(ImageModel),
    editor: PropTypes.instanceOf(EditorModel),
    app: PropTypes.instanceOf(AppModel),
    serverRender: PropTypes.bool,
  }

  static defaultProps = {
    model: ImageModelNULL,
    editor: EditorModelNULL,
    app: AppModelNULL,
    serverRender: false,
  }

  disposeReaction = null

  constructor(props) {
    super(props)

    this.el = React.createRef()

    this.editingSettings = this.props.app?.editingSettings || BlockModelNULL

    this.disposeReaction = reaction(
      () => this.editingSettings,
      (values) => {
        this.setContainerDimentions()
      }
    )

    // Toggling fullWidth changes the padding reduction in setContainerDimentions
    // but does NOT physically resize the observed tableCell, so ResizeObserver
    // won't fire. A mobx reaction (not componentDidUpdate prevProps) is the
    // correct hook here — under @observer, prevProps.model and this.props.model
    // reference the same instance, so prevProps.model.settings.values.fullWidth
    // resolves to the new value too and the comparison would always be equal.
    const model = props.model
    this.disposeFullWidthReaction = reaction(
      () => model.settings.values.fullWidth,
      () => {
        this.setContainerDimentions()
      }
    )

    this.disposeImageUrlReaction = reaction(
      () => model.settings.values.imageUrl,
      () => {
        if (this.state.imgError) this.setState({ imgError: false })
      }
    )
  }

  componentDidMount() {
    this.tableCell = this.getTableCell()
    this.setContainerDimentions()
    if (this.tableCell) {
      this.resizeObserver = new ResizeObserver(() => this.setContainerDimentions())
      this.resizeObserver.observe(this.tableCell)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    this.editingSettings = this.props.app?.editingSettings || BlockModelNULL
  }

  handleImgError = () => {
    if (!this.state.imgError) this.setState({ imgError: true })
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    if (this.disposeReaction) {
      this.disposeReaction()
    }
    if (this.disposeFullWidthReaction) {
      this.disposeFullWidthReaction()
    }
    if (this.disposeImageUrlReaction) {
      this.disposeImageUrlReaction()
    }
  }

  getTableCell = () => {
    return get(this.el, 'current.parentNode')
  }

  setContainerDimentions() {
    const section = get(this.props, 'model.section')
    const columnIndex = this.props.model.columnIndex
    const kind = this.props.model.kind
    const fullWidth = this.props.model.settings.values.fullWidth

    const padding = getPaddingReduction({ kind, section, columnIndex, fullWidth })

    const tableCell = this.tableCell
    const clientWidth = get(tableCell, 'clientWidth')
    this.props.model.setContainerDimensions({
      width: clientWidth - padding,
    })
  }

  render() {
    const { model, serverRender } = this.props
    const globalSettings = this.props.editor.settings.values
    const settings = model.settings.values
    const width = parseFloat(settings.width) || undefined
    const imageLink = settings.imageLink ? settings.imageLink.trim() : ''
    const cropImageUrl =
      model.cropImageUrl && model.isCropping ? model.cropImageUrl.trim() : ''
    const trimmedImageUrl = settings.imageUrl ? settings.imageUrl.trim() : ''
    // cropImageUrl is a locally-generated `blob:` URL (URL.createObjectURL),
    // which validateUrl rejects. Trust it directly and only validate the
    // user-provided imageUrl. isPlaceholder gates onError so the placeholder
    // <img> doesn't run its own load-fail handler if the CDN is unreachable.
    let imageUrl
    let isPlaceholder
    if (cropImageUrl) {
      imageUrl = cropImageUrl
      isPlaceholder = false
    } else if (isHttpImageUrl(trimmedImageUrl) && !this.state.imgError) {
      imageUrl = trimmedImageUrl
      isPlaceholder = false
    } else {
      imageUrl = IMAGE_PLACEHOLDER_PATH
      isPlaceholder = true
    }
    const imgOnError =
      serverRender || isPlaceholder ? undefined : this.handleImgError
    // Reserve the rendered box from the intrinsic dimensions captured at
    // image-set time so a blocked/images-off image doesn't collapse to 0px
    // (FE-1128). Skipped for the placeholder (always loads) and when dimensions
    // aren't known yet.
    const reservedHeight = isPlaceholder
      ? undefined
      : reservedImageHeight(width, settings.naturalWidth, settings.naturalHeight)

    const altText = settings.altText ? settings.altText.trim() : ''

    const fontSize = settings.fontSize || globalSettings.normalText_fontSize

    const fullWidthClass = this.props.model.settings.values.fullWidth
      ? ' full-width'
      : ''
    const mobileFullWidthClass = this.props.model.settings.values.mobileFullWidth
    ? ' mobile-full-width'
    : ''
    const className = `${serverRender ? '' : 'image'}${fullWidthClass}${mobileFullWidthClass}`

    const imgStyle = {
      minHeight: serverRender ? undefined : '20px',
      display: 'block',
      border: 0,
      height: 'auto',
      margin: 0,
      width: width ? `${width}px` : undefined,
      maxWidth: width ? `100%` : undefined,
      fontSize: fontSize + 'px',
    }

    return (
      <Choose>
        <When condition={imageLink}>
          <a
            href={imageLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'block' }}
            ref={this.el}
          >
            <img
              className={className}
              src={imageUrl}
              alt={altText}
              width={width}
              height={reservedHeight}
              style={imgStyle}
              onError={imgOnError}
            />
          </a>
        </When>
        <When condition={!imageLink}>
          <img
            className={className}
            src={imageUrl}
            alt={altText}
            width={width}
            height={reservedHeight}
            style={imgStyle}
            ref={this.el}
            onError={imgOnError}
          />
        </When>
      </Choose>
    )
  }
}

export default inject('app', 'editor', 'serverRender')(observer(Image))
